import { useState, useEffect, useCallback, useMemo } from 'react';
import { getLookups } from '../api/lookups';
import type { LookupDto } from '../types/lookup';

interface UseLookupsOptions {
  includeInactive?: boolean;
}

/**
 * Hook for fetching lookup items for a given type
 */
export function useLookups(type: string, options?: UseLookupsOptions) {
  const [data, setData] = useState<LookupDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const includeInactive = options?.includeInactive ?? false;

  // Memoize options to avoid unnecessary re-renders
  const optionsKey = useMemo(
    () => JSON.stringify({ type, includeInactive }),
    [type, includeInactive]
  );

  const fetchLookups = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const lookups = await getLookups(type, includeInactive);
      setData(lookups);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch lookup items');
    } finally {
      setIsLoading(false);
    }
  }, [type, includeInactive]);

  useEffect(() => {
    fetchLookups();
  }, [optionsKey, fetchLookups]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchLookups,
    setData,
  };
}
